import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal, useMediaUrl } from '@cuito/ui';
import type { ProductDto, PromotionSchemeDto } from '@cuito/types';
import { catalogApi } from '../../api/catalog';
import { uploadFile } from '../../api/files';
import { shelvesApi, type GridCellView, type GridShelfView } from '../../api/shelves';
import { readServerMessage } from '../../lib/apiError';
import {
  buildAdjustBody,
  buildCreateItemBody,
  buildUpdateItemBody,
  showsPurchaseLimit,
  showsQuantity,
  type CellDraft,
  type CellKind,
} from '../../lib/cellEditorForm';
import { PRICE_UNAVAILABLE_TEXT, formatCents, formatServerYuan } from '../../lib/schemeForm';

interface Props {
  open: boolean;
  /** 当前货架（来自 `GET /locations/:id/grid` 的那一条）；null 表示没有选中货架 */
  shelf: GridShelfView | null;
  onClose: () => void;
  /** 任何写操作成功后由页面重新拉网格，新数据以 props 回流到本组件 */
  onChanged: () => void;
}

const EMPTY_DRAFT: CellDraft = { quantity: '0', purchaseLimit: '', sortOrder: '0' };

/**
 * 服务端格子 → 行内草稿（受控输入一律存字符串）。
 *
 * 抽成函数是为了让「初值」与「回流时的同步」用同一段代码：两处各写一遍的话，
 * 迟早出现「初次挂载显示正确、重拉之后显示错」这种只在第二次渲染才暴露的偏差。
 */
function draftFromServer(
  shelfQuantity: number | null,
  purchaseLimit: number | null,
  sortOrder: number,
): CellDraft {
  return {
    quantity: String(shelfQuantity ?? 0),
    purchaseLimit: purchaseLimit === null ? '' : String(purchaseLimit),
    sortOrder: String(sortOrder),
  };
}

interface ItemRowProps {
  item: GridCellView;
  saving: boolean;
  onUpdate: (shelfItemId: string, kind: CellKind, next: CellDraft) => Promise<void>;
  onAdjust: (shelfItemId: string, deltaText: string, reason: string) => Promise<void>;
  onRemove: (shelfItemId: string) => Promise<void>;
}

/**
 * 已上架格子的一行。
 *
 * **草稿状态放在行内**而不是提到 Modal 顶层：提到顶层会让「点第 2 行的保存」
 * 把第 1 行残留的输入写进第 2 行——数据上表现为「改错了行」，而界面上看不出来。
 *
 * 金额用 `lib/schemeForm` 的 `formatServerYuan`（→ `@cuito/ui` 的 `formatYuan`，§7.8 的
 * `¥1,280.00`）把服务端的字符串十进制转成分再格式化：`Number(price) * 100` 会在 `19.99`
 * 上先得到 `1998.9999…`，一旦漏掉取整就丢 1 分。读不出来渲染占位文案，**不编 `¥0.00`**。
 */
function ItemRow({ item, saving, onUpdate, onAdjust, onRemove }: ItemRowProps) {
  const kind: CellKind = item.schemeId === null ? 'PRODUCT' : 'PROMOTION';
  const { shelfQuantity, purchaseLimit, sortOrder } = item;
  const [draft, setDraft] = useState<CellDraft>(() =>
    draftFromServer(shelfQuantity, purchaseLimit, sortOrder),
  );
  const [adjusting, setAdjusting] = useState(false);
  const [deltaText, setDeltaText] = useState('');
  const [reason, setReason] = useState('');

  /**
   * 服务端值回流时把草稿同步过来。
   *
   * **不同步的后果是静默写回旧值**：「增减数量」成功后 `onChanged()` 让页面重拉网格，
   * 新数据以 props 回流，而这一行的 key 是 `shelfItemId`（增减不换 id）→ 组件不重挂载，
   * 行内草稿仍是挂载时那份。实测表现：点 +5 后摘要显示「摆放 17 件」，输入框还是 12，
   * 再点「保存」就把 12 写回去，用户以为保存无效（或数量被改回去了）。
   *
   * 依赖只列这三个**原始值**而不是整个 `item`：网格每重拉一次 `item` 都是新对象，
   * 依赖它会连带把用户正在输入的内容清掉（例如上一行的增减触发重拉时）。
   */
  useEffect(() => {
    setDraft(draftFromServer(shelfQuantity, purchaseLimit, sortOrder));
  }, [shelfQuantity, purchaseLimit, sortOrder]);

  return (
    <div
      data-testid={`cell-item-${item.shelfItemId}`}
      className="flex flex-col gap-2 rounded-lg border-2 border-black p-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-bold">
          {kind === 'PROMOTION' && (
            <span className="mr-2 rounded bg-black px-1.5 py-0.5 text-[10px] font-bold text-white">
              组合促销
            </span>
          )}
          {item.name}
        </span>
        <span className="text-sm text-gray-500">
          {formatServerYuan(item.price) ?? PRICE_UNAVAILABLE_TEXT} · 可用 {item.availableQty} 件
          {item.shelfQuantity === null ? '' : ` · 摆放 ${item.shelfQuantity} 件`}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        {showsQuantity(kind) && (
          <Input
            label="摆放数量"
            className="w-24"
            inputMode="numeric"
            value={draft.quantity}
            onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
          />
        )}
        {showsPurchaseLimit(kind) && (
          <Input
            /*
             * 这里是 `PATCH /shelf-items/:id`：`purchaseLimit` 是可空列（`Int?`），
             * 留空 → `buildUpdateItemBody` 显式发 `null` → 服务端写库 NULL = **不限购**
             * （附录 B 偏差 59）。文案必须写清「留空即不限购」：写「保持原值」会让用户
             * 以为清不掉，而写「不限」又不说明它真的会落库（旧版文案正是前者，
             * 因为当时服务端把 `null` 判成 400）。
             */
            label="购买上限（留空即不限购）"
            className="w-32"
            inputMode="numeric"
            value={draft.purchaseLimit}
            onChange={(e) => setDraft({ ...draft, purchaseLimit: e.target.value })}
          />
        )}
        <Input
          label="排序"
          className="w-20"
          inputMode="numeric"
          value={draft.sortOrder}
          onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
        />
        <Button size="sm" onClick={() => void onUpdate(item.shelfItemId, kind, draft)} disabled={saving}>
          保存
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setAdjusting((value) => !value)}
          disabled={saving || kind === 'PROMOTION'}
        >
          增减数量
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={() => void onRemove(item.shelfItemId)}
          disabled={saving}
        >
          下架
        </Button>
      </div>

      {adjusting && (
        <div className="flex flex-wrap items-end gap-2 border-t-2 border-black pt-2">
          <Input
            label="增减（正数补货，负数撤货）"
            className="w-40"
            inputMode="numeric"
            value={deltaText}
            onChange={(e) => setDeltaText(e.target.value)}
          />
          <Input label="原因" className="w-48" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button
            size="sm"
            onClick={() => void onAdjust(item.shelfItemId, deltaText, reason)}
            disabled={saving}
          >
            提交
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAdjusting(false)}>
            取消
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * 货架单元格编辑器（`05-ui-design-system.md` §2.2.2 R1、§3.2 `/locations/:id/shelves`）。
 *
 * 相对旧 `admin/CellEditorModal.tsx` 的口径变化：
 *   · 从「场次 + 行列」改为**货架实体**：写操作全部打在 `/shelves/:id/*` 与
 *     `/shelf-items/:id/*` 上（契约 §7 端点表 11/14–17）；
 *   · **方案格子没有数量与购买上限输入框**（方案不持有库存），请求体里也绝不带这两个字段；
 *   · 图标按**货架**上传（`PUT /shelves/:id/icon`，走 §17.1 的 `shelf_icons` 白名单）。
 *
 * 格子数据**用 props 里的网格条目**（`GridCellView`：带商品名、价格、可用量），
 * 不用 `GET /shelves/:id` 的 `items`——后者是 `ShelfItem` 本体（`{id, quantity, …}`），
 * 没有展示字段，拿它渲染会得到一列空白。
 *
 * 金额一律走 `lib/schemeForm` 的 `formatCents`（§7.8 要求带千分位），
 * 本文件**不新写第二个金额格式化函数**。
 */
export default function CellEditorModal({ open, shelf, onClose, onChanged }: Props) {
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [schemes, setSchemes] = useState<PromotionSchemeDto[]>([]);
  /**
   * 商品库/方案库的总条数。
   *
   * 契约的分页上限就是 100（`04-api-contract.md` §1.3），一次只能拿一页；
   * 丢掉 `total` 就会出现「第 101 个商品永远选不到，而且界面上没有任何提示」——
   * 用户只会以为那个商品不存在。拿到总数才能在截断时说出真相并引导用搜索。
   */
  const [productTotal, setProductTotal] = useState(0);
  const [schemeTotal, setSchemeTotal] = useState(0);
  /** 输入框里的关键词（受控）与已经生效的关键词（按下搜索/回车才生效，避免每敲一个字打一次请求） */
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<CellKind>('PRODUCT');
  const [productId, setProductId] = useState('');
  const [schemeId, setSchemeId] = useState('');
  const [draft, setDraft] = useState<CellDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const iconUrl = useMediaUrl(shelf?.iconUrl ?? null);

  /**
   * 商品与方案是「从商品库添加」的下拉数据源，与货架本身无关。
   *
   * 带 `keyword` 重新拉：`pageSize` 的上限是 100，没有搜索时第 101 条之后的选择
   * 根本不可能完成（`catalogApi` 早已支持 `keyword`，只是没人调用）。
   */
  const loadCatalog = useCallback(async (search: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const trimmed = search.trim();
      const params = { pageSize: 100, ...(trimmed === '' ? {} : { keyword: trimmed }) };
      const [productPage, schemePage] = await Promise.all([
        catalogApi.listProducts(params),
        catalogApi.listSchemes(params),
      ]);
      setProducts(productPage.items);
      setProductTotal(productPage.total);
      setSchemes(schemePage.items);
      setSchemeTotal(schemePage.total);
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      /*
       * 关闭时把**全部**本地状态复位。
       *
       * 只清 `draft/productId/schemeId` 的话，上一次的失败提示与「类型」选择会跟着
       * 弹窗一起复活：上架失败 → 关弹窗 → 打开另一个货架，顶部还挂着上一条与自己
       * 无关的红色报错，而「类型」还停在促销方案（用户以为默认是单品）。
       * `error` 的清理尤其重要：弹窗一打开就会重新拉商品库，那条旧错误会一直
       * 挂到下一次写操作开始为止。关键词同理：它是「上一个货架上做的搜索」。
       */
      setDraft(EMPTY_DRAFT);
      setProductId('');
      setSchemeId('');
      setError(null);
      setKind('PRODUCT');
      setKeyword('');
      setAppliedKeyword('');
      return;
    }
    void loadCatalog(appliedKeyword);
  }, [open, appliedKeyword, loadCatalog]);

  const addItem = async (): Promise<void> => {
    if (!shelf) return;
    const built = buildCreateItemBody({
      kind,
      ...(productId === '' ? {} : { productId }),
      ...(schemeId === '' ? {} : { schemeId }),
      draft,
    });
    if (!built.ok) {
      setError(built.error);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await shelvesApi.addItem(shelf.shelfId, built.body);
      setDraft(EMPTY_DRAFT);
      setProductId('');
      setSchemeId('');
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '上架失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = async (
    shelfItemId: string,
    itemKind: CellKind,
    next: CellDraft,
  ): Promise<void> => {
    const built = buildUpdateItemBody(itemKind, next);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await shelvesApi.updateItem(shelfItemId, built.body);
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const adjust = async (shelfItemId: string, deltaText: string, reason: string): Promise<void> => {
    const built = buildAdjustBody(deltaText, reason);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await shelvesApi.adjustQuantity(shelfItemId, {
        delta: built.body.delta as number,
        reason: built.body.reason as string,
      });
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '数量调整失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (shelfItemId: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await shelvesApi.removeItem(shelfItemId);
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '下架失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const uploadIcon = async (file: File): Promise<void> => {
    if (!shelf) return;
    setSaving(true);
    setError(null);
    try {
      const uploaded = await uploadFile(file, 'shelf_icons');
      await shelvesApi.setIcon(shelf.shelfId, uploaded.url);
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '图标上传失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const removeIcon = async (): Promise<void> => {
    if (!shelf) return;
    setSaving(true);
    setError(null);
    try {
      await shelvesApi.deleteIcon(shelf.shelfId);
      onChanged();
    } catch (e) {
      setError(readServerMessage(e) ?? '清除图标失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={shelf ? `货架 ${shelf.code}` : '货架'}
      maxWidth="max-w-2xl"
    >
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
        {error && (
          <p
            data-testid="cell-editor-error"
            className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
          >
            {error}
          </p>
        )}

        {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}

        {!loading && shelf && (
          <>
            <section className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-black p-3">
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={`货架 ${shelf.code} 图标`}
                  className="h-16 w-16 rounded border object-cover"
                />
              ) : (
                <span className="text-sm text-gray-500">尚未设置图标</span>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadIcon(file);
                  e.target.value = '';
                }}
                className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-sm"
              />
              {shelf.iconUrl !== null && (
                <Button variant="danger" size="sm" onClick={() => void removeIcon()} disabled={saving}>
                  清除图标
                </Button>
              )}
            </section>

            <section className="flex flex-col gap-2">
              {shelf.items.length === 0 ? (
                <p className="py-8 text-center text-lg text-gray-500">该位置暂无商品</p>
              ) : (
                shelf.items.map((item) => (
                  <ItemRow
                    key={item.shelfItemId}
                    item={item}
                    saving={saving}
                    onUpdate={updateItem}
                    onAdjust={adjust}
                    onRemove={removeItem}
                  />
                ))
              )}
            </section>

            <section className="flex flex-col gap-2 rounded-lg border-2 border-black p-3">
              <h3 className="font-bold">添加格子</h3>

              {/*
                商品库/方案库的搜索：一次最多只能取 100 条（契约 §1.3 的 pageSize 上限），
                没有它第 101 个商品就永远选不到。按下按钮或回车才发请求（`appliedKeyword`），
                避免每敲一个字打一次接口。
              */}
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  label="搜索商品库 / 方案库"
                  className="w-64"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setAppliedKeyword(keyword);
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  className="min-h-[44px]"
                  onClick={() => setAppliedKeyword(keyword)}
                  disabled={loading}
                >
                  搜索
                </Button>
                {appliedKeyword !== '' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="min-h-[44px]"
                    onClick={() => {
                      setKeyword('');
                      setAppliedKeyword('');
                    }}
                    disabled={loading}
                  >
                    清除搜索
                  </Button>
                )}
              </div>

              {/* 截断必须说出来：不提示的话用户只会以为那个商品不存在 */}
              {kind === 'PRODUCT' && productTotal > products.length && (
                <p data-testid="catalog-truncated" className="text-sm text-gray-500">
                  共 {productTotal} 个商品，仅显示前 {products.length} 个，请用关键词搜索
                </p>
              )}
              {kind === 'PROMOTION' && schemeTotal > schemes.length && (
                <p data-testid="catalog-truncated" className="text-sm text-gray-500">
                  共 {schemeTotal} 个促销方案，仅显示前 {schemes.length} 个，请用关键词搜索
                </p>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-bold">类型</span>
                  <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as CellKind)}
                    className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="PRODUCT">单品</option>
                    <option value="PROMOTION">促销方案</option>
                  </select>
                </label>

                {kind === 'PRODUCT' ? (
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">商品</span>
                    <select
                      value={productId}
                      onChange={(e) => setProductId(e.target.value)}
                      className="min-h-[44px] w-64 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                    >
                      <option value="">请选择商品</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">促销方案</span>
                    <select
                      value={schemeId}
                      onChange={(e) => setSchemeId(e.target.value)}
                      className="min-h-[44px] w-64 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                    >
                      <option value="">请选择促销方案</option>
                      {schemes.map((scheme) => (
                        <option key={scheme.id} value={scheme.id}>
                          {scheme.title}（{formatCents(scheme.priceCents)}）
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {showsQuantity(kind) && (
                  <Input
                    label="摆放数量"
                    className="w-24"
                    inputMode="numeric"
                    value={draft.quantity}
                    onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                  />
                )}
                {showsPurchaseLimit(kind) && (
                  <Input
                    /*
                     * 这里是 `POST /shelves/:id/items`（新建格子）：留空 = 请求体里不带
                     * `purchaseLimit` = 新格子没有上限。语义与行内那份一致（都是「不限」），
                     * 只是表达不同 —— 新建侧省略字段（本来就落 NULL），更新侧显式发 `null`
                     * （PATCH 里省略 = 保持原值，清不掉）。
                     */
                    label="购买上限（留空不限）"
                    className="w-32"
                    inputMode="numeric"
                    value={draft.purchaseLimit}
                    onChange={(e) => setDraft({ ...draft, purchaseLimit: e.target.value })}
                  />
                )}
                <Input
                  label="排序"
                  className="w-20"
                  inputMode="numeric"
                  value={draft.sortOrder}
                  onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
                />
                <Button onClick={() => void addItem()} disabled={saving} className="disabled:opacity-50">
                  {saving ? '提交中…' : '添加'}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                方案格子的排序由服务端归零并对其余项 +1（置顶）；方案不持有库存，
                因此没有数量与购买上限（§7.1 的样例里两者恒为 null）。
              </p>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
